import React, { useCallback } from 'react';
import styles from './style.module.scss';
import { buildClassName } from 'utils/build-class-name';
import ListingStatus from 'components/listing-status';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import DateText from 'components/date-text';
import { listingPageModalIds } from 'constants/test-constants';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { PriceHistoryRowProps } from './types';
import { getSoldPriceLabel, getStatusClass } from './utils';
import ContactForPrice from './ContactForPrice';
import { StandardStatus } from '@zoocasa/go-search';
import { isClientSide } from 'utils/host-config';

//#region sub-components

function ListDate({ isBlurred, listDate } : { isBlurred: boolean; listDate?: Date | null }) {
  const { t } = useTranslation(Namespaces.COMMON);
  const template = t(LOCALE_COMMON_PATHS.PRICE_HISTORY_LIST_DATE_TEMPLATE, { defaultValue: 'MMM D, YYYY' });

  const dateRender = (formattedDate: string) => {
    return (
      <div className={`${isBlurred && styles.blur}` } data-testid={listingPageModalIds.priceHistoryRowDate }>
        {isBlurred ?
          t(LOCALE_COMMON_PATHS.DEFAULTS_DATE_PLACEHOLDER, { defaultValue: '00/00/0000' }) :
          listDate ?
            formattedDate :
            t(LOCALE_COMMON_PATHS.PRICE_HISTORY_NO_DATA_PLACEHOLDER, { defaultValue: '-' })
        }
      </div>
    );
  };
  return (
    <DateText date={listDate} template={template} render={dateRender} />
  );
}

function ListPrice({ isBlurred, listPrice } : { isBlurred: boolean; listPrice?: number | null }) {
  const { t } = useTranslation(Namespaces.COMMON);
  const testId = listingPageModalIds.priceHistoryRowListPrice;
  const { formatCurrency } = useI18nFormatting();
  return (
    <div className={`${isBlurred && styles.blur}`} data-testid={testId}>
      { isBlurred ?
        t(LOCALE_COMMON_PATHS.DEFAULTS_PRICE_PLACEHOLDER, { defaultValue: '$XXX,XXX' }) :
        listPrice > 0 ? formatCurrency(listPrice) : t(LOCALE_COMMON_PATHS.PRICE_HISTORY_NO_DATA_PLACEHOLDER, { defaultValue: '-' })
      }
    </div>
  );
}

function Status({ isBlurred, status, isRental } : { isBlurred: boolean; status: StandardStatus; isRental: boolean }) {
  const { t } = useTranslation(Namespaces.COMMON);
  const testId = listingPageModalIds.priceHistoryRowListStatus;
  return (
    <div className={`${isBlurred && styles.blur}`} data-testid={testId}>
      { isBlurred ?
        t(LOCALE_COMMON_PATHS.PRICE_HISTORY_STATUS_PLACEHOLDER, { defaultValue: 'Top Secret' }) :
        <ListingStatus status={status} isRental={isRental} className={styles['status-label']} />
      }
    </div>
  );
}

function SoldDate({ isBlurred, soldDate } : { isBlurred: boolean; soldDate?: Date | null }) {
  const { t } = useTranslation(Namespaces.COMMON);
  const template = t(LOCALE_COMMON_PATHS.PRICE_HISTORY_SOLD_DATE_TEMPLATE, { defaultValue: 'MMM D, YYYY' });
  const dateRender = (formattedDate: string) => {
    return (
      <div className={`${isBlurred && styles.blur}`} data-testid={listingPageModalIds.priceHistoryRowSoldDate}>
        {isBlurred ?
          t(LOCALE_COMMON_PATHS.DEFAULTS_DATE_PLACEHOLDER, { defaultValue: '00/00/0000' }) :
          soldDate ?
            formattedDate :
            t(LOCALE_COMMON_PATHS.PRICE_HISTORY_NO_DATA_PLACEHOLDER, { defaultValue: '-' })
        }
      </div>
    );
  };
  return (
    <DateText date={soldDate?.toISOString() || ''} template={template} render={dateRender} />
  );
}

function SoldPrice({ isBlurred, soldPrice } : { isBlurred: boolean; soldPrice?: string | null }) {
  const { t } = useTranslation(Namespaces.COMMON);
  const testId = listingPageModalIds.priceHistoryRowSoldPrice;
  return (
    <div className={`${isBlurred && styles.blur}`} data-testid={testId}>
      {isBlurred ?
        t(LOCALE_COMMON_PATHS.DEFAULTS_PRICE_PLACEHOLDER, { defaultValue: '$XXX,XXX' }) :
        soldPrice
      }
    </div>
  );
}

function ViewButton({ isCurrentListing, onViewListingClick } : { isCurrentListing: boolean; onViewListingClick: () => void }) {
  const { t } = useTranslation(Namespaces.COMMON);
  return (
    <div>
      {!isCurrentListing &&
      <button onClick={onViewListingClick}>
        {t(LOCALE_COMMON_PATHS.PRICE_HISTORY_VIEW_BUTTON_LABEL, { defaultValue: 'View' })}
      </button>
      }
    </div>
  );
}

//#endregion

const useIsomorphicLayoutEffect = isClientSide() ? React.useLayoutEffect : () => { /* useLayoutEffect does nothing on the server */ };

export default function PriceHistoryRowDesktop(props: PriceHistoryRowProps) {
  const { t } = useTranslation(Namespaces.COMMON);
  const { formatCurrency } = useI18nFormatting();
  const { listing, isBlurred = false, isCurrentListing = false, testId, onViewListingClick, onContactForPriceClick } = props;
  const soldDate = listing.soldAt || listing.expiredAt;
  const soldPrice = getSoldPriceLabel(t, isCurrentListing, listing.isRental, listing.isAvailable, listing.soldPrice, formatCurrency);

  // We don't want to show the contact for price button when the price is hidden for the user or we have a valid price.
  const showContactForPrice = listing.price === 0 && !isBlurred && isCurrentListing;

  const onViewListingClickRef = React.useRef(onViewListingClick);

  useIsomorphicLayoutEffect(() => {
    onViewListingClickRef.current = onViewListingClick;
  });

  const handleViewListingClick = useCallback(() => {
    onViewListingClickRef?.current?.(listing.addressPath, listing.id);
  }, [listing.addressPath, listing.id]);

  // const handleContactForPriceClick = () => {
  // // const handleContactForPriceClick = () => {
  // //   // const prefillData = { requestViewing: false, isShowingAdditionalComments: true, sourceComments: "I'd like to know the price of this property." };

  // //   // if (isMobile) {
  // //   //   openModal('connection-request', { listing: listing, isViewingRequest: true, customButtonText: 'Request Info', prefillData: prefillData });
  // //   // } else {
  // //   //   openModal('connection-request-popup', { listing: listing, isViewingRequest: true, customButtonText: 'Request Info', prefillData: prefillData });
  // //   // }
  // // };


  // const isNotCurrentlyForSale = listing.status !== 'available';
  // const externalUrl = isNotCurrentlyForSale ? listing.path : listing.addressPathWithFallback;

  //const soldPrice = getSoldPriceLabel(t, isCurrentListing, listing.isRental, listing.isAvailable, listing.soldPrice);
  // const clickViewListing = () => {
  //   if (!isModal) {
  //     // Only track the GTM event on address pages
  //     trackEvent(GTM_CLICK_ADDRESS_PAGE_PRICE_HISTORY_LISTING);
  //   }
  //   openModal('listing', { listingId: listing.id, isHistorical: isNotCurrentlyForSale, externalUrl, isFromPriceHistory: true });
  // };
  // const isBlurred = !isVisible || (!isAuthenticated && listing.isVow) || (!isAuthenticated && listing.isTrebIdxInactive);

  return (
    <div className={`${styles.grid} ${styles.row} ${isCurrentListing && styles.active}`} data-testid={`${testId}-desktop`}>
      <span className={buildClassName(styles.node, getStatusClass(listing.standardStatus))}/>
      <ListDate isBlurred={isBlurred} listDate={listing.addedAt} />
      { showContactForPrice ?
        <ContactForPrice onClick={onContactForPriceClick} /> :
        <ListPrice isBlurred={isBlurred} listPrice={listing.price} />
      }
      <Status isBlurred={isBlurred} status={listing.standardStatus} isRental={listing.isRental} />
      <SoldDate isBlurred={isBlurred} soldDate={soldDate} />
      <SoldPrice isBlurred={isBlurred} soldPrice={soldPrice} />
      <ViewButton isCurrentListing={isCurrentListing} onViewListingClick={handleViewListingClick} />
    </div>
  );
}
